import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AuthenticationService} from '../authentication.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Observable, of, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, mergeMap, take, takeUntil, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {PermissionService} from '../permission.service';
import {ActivatedRoute, Data, NavigationEnd, Router} from '@angular/router';
import {ConfirmDialogService} from '../../shared/confirm-dialog.service';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {LabelingService} from '../../shared/labeling.service';
import {HelpdeskTicketComponent} from '../helpdesk-ticket/helpdesk-ticket.component';
import {HelpersService} from 'src/app/shared/helpers.service';
import {HttpCancelService} from '../httpcancel.service';
import {GenericDialogService} from '../../shared/generic-dialog.service';
import {WhatsNewDisplayComponent} from "../../whats-new/whats-new-display/whats-new-display.component";
import {MatDialog} from "@angular/material/dialog";
import {WhatsNewContentService} from "../../whats-new/whats-new-content.service";
import {FeatureTogglesService} from '../feature-toggles.service';
import {LookerEmbedService} from '../../reporting/looker-dashboards/looker-embed.service';
import {isBypLookerOrganization} from '../../reporting/looker-dashboards/looker-dashboards.config';


@Component({
    selector: 'sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    standalone: false
})
export class SidenavComponent implements OnInit, OnDestroy {

  canUpdatePermissions = false;
  private orgId: number;
  private destroyed$: Subject<void> = new Subject<void>();
  organizationForm: FormGroup;
  private routeData: Data;

  @Input() whatsNewHighlighting;
  @Input() whatsNewMenuDisplay;
  @Output() whatsNewSectionOpened = new EventEmitter();
  storeCompletedLabel: Observable<any>;

  constructor(
    private fb: FormBuilder,
    public router: Router,
    private route: ActivatedRoute,
    private confirmDialog: ConfirmDialogService,
    public authenticationService: AuthenticationService,
    public translateService: TranslateService,
    public labelingService: LabelingService,
    public permissionService: PermissionService,
    public featureTogglesService: FeatureTogglesService,
    public helpersService: HelpersService,
    private httpCancelService: HttpCancelService,
    private genericDialogService: GenericDialogService,
    private whatsNewService: WhatsNewContentService,

    private matDialog: MatDialog,
    private lookerEmbedService: LookerEmbedService,
  ) {
    this.labelingService.getStoreSentenceLabel(marker('Store Completed'), marker('Completed'));
  }

  ngOnInit() {
    this.organizationForm = this.fb.group({
      activeOrganization: [this.authenticationService.selectedOrganization, Validators.required],
    });

    this.canUpdatePermissions = this.authenticationService.organizationData.canUpdatePermissions;

    this.onOrganizationChange();
    this.checkActiveOrganizationStatus();
    this.subscribeToRouteData();

    this.authenticationService.organizationDataUpdatedEvent.subscribe(() => {
      this.canUpdatePermissions = this.authenticationService.organizationData.canUpdatePermissions;
    });

  }
  onCreateHelpdeskTicket() {
    this.genericDialogService.open(HelpdeskTicketComponent);
  }


  viewWhatsNew() {
    this.matDialog.open(WhatsNewDisplayComponent, {
      height: 'fit-content',
      width: '1000px',
      autoFocus: false,
      panelClass: 'dialog--no-padding',
    }).afterClosed().subscribe(() =>
    {
      this.whatsNewHighlighting = false;
      this.whatsNewSectionOpened.emit();
    });
  }

  checkActiveOrganizationStatus() {
    this.authenticationService.activeOrganizationInfo$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe(value => {
        this.orgId = value;
        this.organizationForm.get('activeOrganization').setValue(value);
      });
  }

  onOrganizationChange() {
    this.organizationForm.get('activeOrganization').valueChanges
      .pipe(
        filter(organizationId => !!organizationId),
        distinctUntilChanged(),
        filter(value => value !== this.orgId),
        mergeMap((organizationId) => {
          this.authenticationService.changeOrganizationEvent.next(organizationId);

          if (this.routeData?.currentPage === 'formBuilder') {
            this.httpCancelService.cancelPendingRequests();
            return of(false);
          }

          if (this.routeData?.obsoletedByOrganizationSwitch) {
            return this.confirmDialog.openConfirmDialog(this.translateService.stream(marker('Any unsaved changes will be lost.')))
              .pipe(
                mergeMap(confirm => {
                  if (confirm) {
                    this.httpCancelService.cancelPendingRequests();
                    return this.authenticationService.setActiveOrganization(organizationId).pipe(
                      mergeMap(() => this.router.navigate(['/'])),
                      take(1),
                    );
                  } else {
                    return of(false);
                  }
                }),
              );
          } else {
            return this.authenticationService.setActiveOrganization(organizationId)
              .pipe(take(1));
          }
        }),
        tap(() => this.organizationForm.get('activeOrganization').setValue(this.orgId)),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  logout() {
    if (this.routeData?.mightHavePendingChanges) {
      this.confirmDialog.openConfirmDialog(this.translateService.stream(marker('Any unsaved changes will be lost.')))
        .pipe(
          tap(confirm => {
            if (confirm) {
              this.authenticationService.logout();
            }
          }),
        ).subscribe();
    } else {
      this.authenticationService.logout();
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private subscribeToRouteData(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.route),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      map(route => route.snapshot.data),
    ).subscribe(data => this.routeData = data);
  }

  prefetchLookerSession(dashboardRouteKey?: string): void {
    const orgId = localStorage.getItem('selectedOrganization');
    if (!orgId) {
      return;
    }

    this.permissionService.hasPermission('GRAFANA').pipe(take(1)).subscribe((hasPermission) => {
      if (hasPermission) {
        this.lookerEmbedService.warmCookielessSession(orgId, { dashboardRouteKey });
      }
    });
  }

  showBypLookerDashboards(): boolean {
    return isBypLookerOrganization(
      localStorage.getItem('selectedOrganization') ?? this.authenticationService.selectedOrganization,
    );
  }

  showStandardLookerDashboards(): boolean {
    return !this.showBypLookerDashboards();
  }
}
